import { useEffect, useRef, useState } from 'react';
import FJButton, { FJButtonType } from '../../../common/components/FJButton/FJButton';
import FJInput from '../../../common/components/FJInput/FJInput';
import FJModalClose from '../FJModalClose/FJModalClose';
import styles from './FJRenameModal.module.less';
import FJStringUtil from '@src/util/FJStringUtil';

const RENAME_MAX_BYTE_LEN = 50;

/**
 * 重命名弹窗内容区（与 `FJModal` + `FJRenameModalContainer` 配合；样式对齐设计稿）。
 *
 * @param {object} props
 * @param {string} props.titleText 标题文案（已本地化）
 * @param {string} props.placeholderText 输入框 placeholder（已本地化）
 * @param {string} props.initialValue 初始文件名
 * @param {boolean} [props.visible] 弹层是否展示（与外层一致时可保证每次打开都 focus+全选）
 * @param {() => void} props.onCancel 取消 / 遮罩 / ESC
 * @param {(next: string) => void} props.onConfirm 确认；组件内已 `trim`，空串不会调用
 * @param {() => void} [props.onClose] 右上角关闭，默认同 onCancel
 */
export default function FJRenameModal({
    titleText,
    placeholderText,
    initialValue,
    visible,
    onCancel,
    onConfirm,
    onClose,
}) {
    const [value, setValue] = useState(() => FJStringUtil.truncateToByteLen(initialValue ?? '', RENAME_MAX_BYTE_LEN));
    const inputRef = useRef(/** @type {HTMLInputElement | null} */ (null));
    const closeHandler = onClose ?? onCancel;

    useEffect(() => {
        setValue(FJStringUtil.truncateToByteLen(initialValue ?? '', RENAME_MAX_BYTE_LEN));
    }, [initialValue]);

    useEffect(() => {
        if (visible === false) return;
        /** Chrome 以外：弹层显示/动画后同一宏任务内 focus+select 常无效，需排到绘制后再选区 */
        let raf1 = 0;
        let raf2 = 0;
        const selectAll = () => {
            const el = inputRef.current;
            if (!el) return;
            el.focus();
            const len = el.value.length;
            if (len === 0) return;
            if (typeof el.setSelectionRange === 'function') {
                try {
                    el.setSelectionRange(0, len);
                } catch {
                    el.select();
                }
            } else {
                el.select();
            }
        };
        raf1 = window.requestAnimationFrame(() => {
            raf2 = window.requestAnimationFrame(selectAll);
        });
        return () => {
            window.cancelAnimationFrame(raf1);
            window.cancelAnimationFrame(raf2);
        };
    }, [initialValue, visible]);

    const submit = () => {
        const next = (value ?? '').trim();
        if (!next) return;
        onConfirm(next);
    };

    const onKeyDown = /** @param {import('react').KeyboardEvent<HTMLInputElement>} e */ e => {
        if (e.key === 'Enter') {
            e.preventDefault();
            submit();
        }
    };

    return (
        <div className={styles.fj_rename_modal}>
            <FJModalClose onClick={closeHandler} />
            <h2 className={styles.title}>{titleText}</h2>
            <div className={styles.field}>
                <FJInput
                    ref={inputRef}
                    type='text'
                    value={value}
                    placeholder={placeholderText}
                    aria-label={placeholderText}
                    autoComplete='off'
                    onChange={e => setValue(FJStringUtil.truncateToByteLen(e.target.value, RENAME_MAX_BYTE_LEN))}
                    onKeyDown={onKeyDown}
                />
            </div>
            <div className={styles.footer}>
                <FJButton className={styles.btn_cancel} type={FJButtonType.DEFAULT} text='cancel' onClick={onCancel} />
                <FJButton
                    className={styles.btn_confirm}
                    type={FJButtonType.GRADIENT}
                    text='confirm'
                    disabled={!(value ?? '').trim()}
                    onClick={submit}
                />
            </div>
        </div>
    );
}
